import { useQuery } from "@tanstack/react-query";
import { fetchSellerBadges, type SellerBadges } from "@/lib/trust-badges.functions";
import { VerifiedSellerBadge } from "@/components/verified-seller-badge";
import { OfficialPartnerBadge } from "@/components/official-partner-badge";
import { TopSellerBadge } from "@/components/top-seller-badge";
import { cn } from "@/lib/utils";

/**
 * Renders all trust badges a seller currently holds. Order: Official Partner
 * (highest trust), Verified Seller, Top Seller. Compact on cards, labelled
 * on detail pages.
 */
export function SellerBadgeStack({
  sellerId,
  compact = false,
  size = "sm",
  className,
  badges,
}: {
  sellerId?: string | null;
  compact?: boolean;
  size?: "sm" | "md";
  className?: string;
  /** Optional pre-fetched map to skip the query on list surfaces. */
  badges?: SellerBadges;
}) {
  const enabled = !badges && !!sellerId;
  const { data } = useQuery({
    queryKey: ["seller-badges", sellerId],
    queryFn: () => fetchSellerBadges(sellerId as string),
    enabled,
    staleTime: 60_000,
  });
  const b = badges ?? data;
  if (!b || (!b.officialPartner && !b.verified && !b.top)) return null;
  return (
    <div className={cn("inline-flex flex-wrap items-center gap-1.5", className)}>
      {b.officialPartner && <OfficialPartnerBadge size={size === "md" ? "md" : "sm"} compact={compact} />}
      {b.verified && <VerifiedSellerBadge size={size} />}
      {b.top && <TopSellerBadge size={size} compact={compact} />}
    </div>
  );
}
